import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { publicProductService } from "../services/publicApi";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchAreaRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setShowSuggestions(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isMenuOpen]);

  useEffect(() => {
    const query = search.trim();

    if (query.length < 2) {
      setSearchSuggestions([]);
      setIsSearchLoading(false);
      return undefined;
    }

    let isActive = true;
    const timer = setTimeout(async () => {
      try {
        setIsSearchLoading(true);
        const response = await publicProductService.getSuggestions(query, 6);

        if (!isActive) return;

        setSearchSuggestions(response.suggestions || []);
        setShowSuggestions(true);
      } catch (_) {
        if (!isActive) return;
        setSearchSuggestions([]);
      } finally {
        if (isActive) {
          setIsSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchAreaRef.current && !searchAreaRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollPageToTop = () => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const query = search.trim();
    setShowSuggestions(false);
    scrollPageToTop();
    if (query) {
      navigate(`/produits?search=${encodeURIComponent(query)}`);
    } else {
      navigate('/produits');
    }
    setSearch("");
    setSearchSuggestions([]);
    setShowSuggestions(false);
    setIsMenuOpen(false);
  };

  const clearSearch = () => {
    setSearch('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
    scrollPageToTop();
    navigate('/produits');
    setIsMenuOpen(false);
  };

  const handleSuggestionClick = (product) => {
    setShowSuggestions(false);
    setIsMenuOpen(false);
    setSearch("");
    scrollPageToTop();
    if (product?.slug) {
      navigate(`/produit/${product.slug}`);
      return;
    }
    navigate(`/produits?search=${encodeURIComponent(product?.name || "")}`);
  };

  const renderSuggestions = (className = "") => {
    const query = search.trim();
    if (!showSuggestions || query.length < 2) return null;

    return (
      <div className={`hdr-search-suggestions ${className}`.trim()}>
        {isSearchLoading ? (
          <div className="hdr-search-suggestion hdr-search-suggestion--state">
            Recherche en cours...
          </div>
        ) : searchSuggestions.length > 0 ? (
          <>
            {searchSuggestions.map((product) => (
              <button
                key={product._id || product.slug || product.name}
                type="button"
                className="hdr-search-suggestion"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSuggestionClick(product)}
              >
                <span className="hdr-search-suggestion__main">
                  <i className="fas fa-box-open"></i>
                  <span>
                    {product.name}
                    {product.reference && (
                      <span className="hdr-search-suggestion__ref">{product.reference}</span>
                    )}
                  </span>
                </span>
                <span className="hdr-search-suggestion__arrow">
                  <i className="fas fa-arrow-up-right-from-square"></i>
                </span>
              </button>
            ))}
            <button
              type="button"
              className="hdr-search-suggestion hdr-search-suggestion--all"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => onSearchSubmit(e)}
            >
              Voir tous les résultats pour "{query}"
            </button>
          </>
        ) : (
          <div className="hdr-search-suggestion hdr-search-suggestion--state">
            Aucun produit trouvé
          </div>
        )}
      </div>
    );
  };

  const navLinks = [
    { to: "/", label: "Accueil" },
    { to: "/domoelec", label: "DOMOELEC" },
    { to: "/produits", label: "Produits" },
    { to: "/blog", label: "Blog" },
    { to: "/services", label: "Services" },
  ];

  return (
    <>
      {/* === MAIN HEADER === */}
      <header className={`hdr-main ${scrolled ? 'hdr-scrolled' : ''}`} ref={searchAreaRef}>
        <div className="hdr-shell">
          <div className="hdr-middle">
            <div className="hdr-middle-inner">
              <Link to="/" className="hdr-brand" onClick={scrollPageToTop}>
                <img src="/images/logo-domoelec.png" alt="DOMOELEC" className="hdr-brand-img" />
              </Link>

              <nav className="hdr-nav-inline" aria-label="Navigation principale">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`hdr-nav-link ${location.pathname === link.to ? 'active' : ''}`}
                  onClick={scrollPageToTop}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" className="hdr-nav-cta" onClick={scrollPageToTop}>
                Contactez-nous
              </Link>
              </nav>

              <div className="hdr-toolbar">
                <div className="hdr-search-wrap">
                  <form className="hdr-search" onSubmit={onSearchSubmit} role="search">
                    <i className="fas fa-search hdr-search-icon"></i>
                    <input
                      type="search"
                      placeholder="Rechercher..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onFocus={() => {
                        if (search.trim().length >= 2) setShowSuggestions(true);
                      }}
                      aria-label="Rechercher un produit ou une référence"
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearSearch(); }}
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        className="hdr-search-clear"
                        aria-label="Effacer"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                    <button type="submit" className="hdr-search-btn" aria-label="Lancer la recherche">
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </form>
                  {renderSuggestions()}
                </div>

                <div className="hdr-actions">
                  <a href="tel:0522340659" className="hdr-action-contact" aria-label="Appeler DOMOELEC">
                    <i className="fas fa-phone-alt"></i>
                  </a>
                  <Link to="/contact" className="hdr-action-primary" onClick={scrollPageToTop}>
                    Devis gratuit
                  </Link>
                </div>
              </div>

              <button className={`hdr-hamburger ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu} aria-label="Menu">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line className="hdr-ham-top" x1="3" y1="6" x2="21" y2="6" />
                  <line className="hdr-ham-mid" x1="3" y1="12" x2="21" y2="12" />
                  <line className="hdr-ham-bot" x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="hdr-search-mobile">
          <div className="hdr-search-mobile-wrap">
            <form className="hdr-search-m" onSubmit={onSearchSubmit} role="search">
              <i className="fas fa-search"></i>
              <input
                type="search"
                placeholder="Rechercher un produit..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => {
                  if (search.trim().length >= 2) setShowSuggestions(true);
                }}
                aria-label="Rechercher un produit"
              />
              {search && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearSearch(); }}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  className="hdr-search-clear"
                  aria-label="Effacer"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
              <button type="submit" className="hdr-search-m-btn">
                <i className="fas fa-search"></i>
              </button>
            </form>
            {renderSuggestions("hdr-search-suggestions--mobile")}
          </div>
        </div>
      </header>

      {/* === MOBILE SIDEBAR === */}
      <div className={`hdr-mobile-sidebar ${isMenuOpen ? 'open' : ''}`}>
        <div className="hdr-mobile-sidebar-header">
          <Link to="/" className="hdr-mobile-sidebar-brand" onClick={scrollPageToTop}>
            <img src="/images/logo-domoelec.png" alt="DOMOELEC" className="hdr-mobile-sidebar-logo" />
          </Link>
          <button className="hdr-mobile-sidebar-close" onClick={() => setIsMenuOpen(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <nav className="hdr-mobile-nav">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`hdr-mobile-link ${location.pathname === link.to ? 'active' : ''}`}
              onClick={scrollPageToTop}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="hdr-mobile-link hdr-mobile-link--cta"
            onClick={scrollPageToTop}
          >
            Contactez-nous
          </Link>
          {/* <Link
            to="/admin"
            className={`hdr-mobile-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
          >
            Espace Admin
          </Link> */}
        </nav>
        <div className="hdr-mobile-footer">
          <Link to="/contact" className="hdr-mobile-quote" onClick={scrollPageToTop}>
            <i className="far fa-paper-plane"></i>
            <span>Devis gratuit</span>
          </Link>
          <a href="tel:0522340659" className="hdr-mobile-contact">
            <i className="fas fa-phone-alt"></i>
            <span>05 22 34 06 59</span>
          </a>
          <a href="mailto:info@domoelec.ma" className="hdr-mobile-contact">
            <i className="fas fa-envelope"></i>
            <span>info@domoelec.ma</span>
          </a>
          <div className="hdr-mobile-socials">
            <span className="hdr-mobile-socials-label">Suivez-nous</span>
            <div className="hdr-mobile-socials-list">
              <a href="https://www.linkedin.com/company/domoelecsarl/posts/?feedView=all" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn DOMOELEC">
                <i className="fab fa-linkedin-in"></i>
              </a>
              <a href="https://www.youtube.com/@DOMOELEC-CFA" target="_blank" rel="noopener noreferrer" aria-label="YouTube DOMOELEC">
                <i className="fab fa-youtube"></i>
              </a>
              <a href="https://www.instagram.com/domo.elec/" target="_blank" rel="noopener noreferrer" aria-label="Instagram DOMOELEC">
                <i className="fab fa-instagram"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
      {isMenuOpen && <div className="hdr-overlay" onClick={() => setIsMenuOpen(false)}></div>}
    </>
  );
};

export default Header;
